import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "locales";
import { ChatFolder } from "hooks/useChatFolders";
import "./style.scss";

interface Props {
  folders: ChatFolder[];
  activeFolderId: string | null;
  onSelectFolder: (id: string | null) => void;
  onCreateFolder: (name: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onDeleteFolder: (id: string) => void;
  unreadCounts: Record<string, number>;
}

function ChatFolderTabs({
  folders,
  activeFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  unreadCounts,
}: Props) {
  const { t } = useTranslation();
  const [ctxMenu, setCtxMenu] = useState<{ folderId: string; x: number; y: number } | null>(null);
  // Adjusted position after useLayoutEffect measures the real menu rect.
  // See MessageThreadItem for the full rationale; same containing-block
  // trap (.page-messages__sidebar `will-change: transform`) applies here.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [showNewInput, setShowNewInput] = useState(false);
  const [newName, setNewName] = useState("");
  const newInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close context menu on any click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [ctxMenu]);

  // Measure real menu rect and flip/clamp to viewport. Same pattern as
  // MessageThreadItem — necessary because the raw click position can fall
  // close enough to the bottom/right edge that the menu would crop.
  useLayoutEffect(() => {
    if (!ctxMenu) {
      setMenuPos(null);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const PADDING = 8;
    let top = ctxMenu.y;
    let left = ctxMenu.x;
    if (top + rect.height + PADDING > window.innerHeight) {
      top = Math.max(PADDING, window.innerHeight - rect.height - PADDING);
    }
    if (left + rect.width + PADDING > window.innerWidth) {
      left = Math.max(PADDING, window.innerWidth - rect.width - PADDING);
    }
    setMenuPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [ctxMenu]);

  const handleTabContextMenu = useCallback((e: React.MouseEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Raw click coords — the useLayoutEffect above measures the real menu
    // and flips/clamps it to fit the viewport.
    setCtxMenu({ folderId, x: e.clientX, y: e.clientY });
  }, []);

  const handleRenameStart = useCallback((folderId: string, currentName: string) => {
    setCtxMenu(null);
    setEditingId(folderId);
    setEditName(currentName);
    setTimeout(() => editInputRef.current?.select(), 0);
  }, []);

  const handleRenameSubmit = useCallback(() => {
    if (editingId && editName.trim()) {
      onRenameFolder(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName("");
  }, [editingId, editName, onRenameFolder]);

  const handleDelete = useCallback(
    (folderId: string) => {
      setCtxMenu(null);
      if (window.confirm(t("Delete this folder?"))) {
        onDeleteFolder(folderId);
      }
    },
    [onDeleteFolder, t]
  );

  const handleNewFolderClick = useCallback(() => {
    setShowNewInput(true);
    setNewName("");
    setTimeout(() => newInputRef.current?.focus(), 0);
  }, []);

  const handleNewFolderSubmit = useCallback(() => {
    if (newName.trim()) {
      onCreateFolder(newName.trim());
    }
    setShowNewInput(false);
    setNewName("");
  }, [newName, onCreateFolder]);

  if (folders.length === 0 && !showNewInput) return null;

  return (
    <div className="chat-folder-tabs">
      <div className="chat-folder-tabs__list">
        <button
          type="button"
          className={`chat-folder-tabs__tab${activeFolderId === null ? " __active" : ""}`}
          onClick={() => onSelectFolder(null)}
        >
          {t("All")}
        </button>
        {folders.map((folder) => (
          <button
            key={folder.id}
            type="button"
            className={`chat-folder-tabs__tab${activeFolderId === folder.id ? " __active" : ""}`}
            onClick={() => onSelectFolder(folder.id)}
            onContextMenu={(e) => handleTabContextMenu(e, folder.id)}
          >
            {editingId === folder.id ? (
              <input
                ref={editInputRef}
                className="chat-folder-tabs__edit-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={handleRenameSubmit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRenameSubmit();
                  if (e.key === "Escape") { setEditingId(null); setEditName(""); }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <>
                <span className="chat-folder-tabs__tab-name">{folder.name}</span>
                {(unreadCounts[folder.id] || 0) > 0 && (
                  <span className="chat-folder-tabs__badge">
                    {unreadCounts[folder.id] > 99 ? "99+" : unreadCounts[folder.id]}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
        {showNewInput ? (
          <div className="chat-folder-tabs__new-input-wrapper">
            <input
              ref={newInputRef}
              className="chat-folder-tabs__new-input"
              placeholder={t("Folder name")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleNewFolderSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNewFolderSubmit();
                if (e.key === "Escape") { setShowNewInput(false); setNewName(""); }
              }}
            />
          </div>
        ) : (
          <button type="button" className="chat-folder-tabs__add-btn" onClick={handleNewFolderClick} title={t("New folder")}>
            +
          </button>
        )}
      </div>

      {/* Portal to document.body: .page-messages__sidebar has
          `will-change: transform` which creates a containing block for
          position:fixed descendants — so without portaling, the menu's
          `top: clientY` would be interpreted relative to sidebar, not
          viewport. Same root cause as MessageThreadItem's portal. */}
      {ctxMenu && createPortal(
        <div
          ref={menuRef}
          className="chat-folder-tabs__ctx-menu"
          style={{
            top: menuPos?.top ?? ctxMenu.y,
            left: menuPos?.left ?? ctxMenu.x,
            visibility: menuPos ? "visible" : "hidden",
          }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <button
            type="button"
            onClick={() => {
              const folder = folders.find((f) => f.id === ctxMenu.folderId);
              if (folder) handleRenameStart(ctxMenu.folderId, folder.name);
            }}
          >
            {t("Rename")}
          </button>
          <button type="button" onClick={() => handleDelete(ctxMenu.folderId)}>
            {t("Delete")}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default ChatFolderTabs;
